import styles from "./inputs.module.css"

import React from "react"
import cx from "classnames"
import merge from "lodash/merge"
import assign from "object-assign"
import MaskedInput from "react-text-mask"
import createNumberMask from "text-mask-addons/dist/createNumberMask"
import * as str from "shared/string"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import {Tooltip} from "shared/tooltips"

const colsCls = {
  1: "one-col",
  2: "two-col",
  3: "three-col"
}

export const Tabs = ({count = 2, children, className}) => (
  <div className={cx(styles["tabs"], styles[colsCls[count]], className)} role="tablist">
    {children}
  </div>
)

export const TabsWithLabel = ({count = 2, label, children}) => (
  <div>
    {label && <label className={styles["label"]}>{label}</label>}
    <Tabs className={styles["with-label"]} count={count}>
      {children}
    </Tabs>
  </div>
)

export const Tab = ({children, selected, handleClick}) => (
  <button role="tab" className={cx(styles["tab"], selected && styles["selected"])} onClick={handleClick}>
    {children}
  </button>
)

const amtMask = (integerLimit) =>
  createNumberMask({
    prefix: "$",
    decimalLimit: 0,
    integerLimit
  })

const maskWithSymbol = (integerLimit) =>
  createNumberMask({
    prefix: "$",
    decimalLimit: 0,
    integerLimit
  })

const maskWithPercent = (integerLimit) =>
  createNumberMask({
    prefix: "",
    suffix: "%",
    allowDecimal: true,
    requireDecimal: true,
    integerLimit
  })

const hasValue = (value) => value !== null && value !== undefined && value !== "" && value !== "0"

const valueTypeOptions = {
  "currency": {
    mask: createNumberMask({
      prefix: "$",
      decimalLimit: 0,
      integerLimit: 9
    })
  },
  "percent": {
    mask: createNumberMask({
      prefix: "",
      suffix: "%",
      allowDecimal: true,
      integerLimit: 2,
      decimalLimit: 3
    })
  },
  "percent-100": {
    mask: createNumberMask({
      prefix: "",
      suffix: "%",
      allowDecimal: true,
      integerLimit: 3,
      decimalLimit: 3
    })
  },
  "percent-no-decimal": {
    mask: createNumberMask({
      prefix: "",
      suffix: "%",
      integerLimit: 3,
      decimalLimit: 0
    })
  },
  "years": {
    mask: createNumberMask({
      prefix: "",
      suffix: " yrs",
      integerLimit: 3,
      decimalLimit: 0
    })
  },
  "years-full": {
    mask: createNumberMask({
      prefix: "",
      suffix: " years",
      integerLimit: 3,
      decimalLimit: 0
    })
  },
  "months": {
    mask: createNumberMask({
      prefix: "",
      suffix: " mths",
      integerLimit: 2,
      decimalLimit: 0
    })
  },
  "number": {
    mask: createNumberMask({
      prefix: "",
      suffix: "",
      integerLimit: 3,
      decimalLimit: 0
    })
  },
  "year": {
    mask: createNumberMask({
      prefix: "",
      suffix: "",
      integerLimit: 4,
      decimalLimit: 0,
      includeThousandsSeparator: false
    })
  }
}

const placeholders = [
  [[null, "small"], ""],
  [["currency", "big"], "———,———"],
  [["percent", "big"], "%"],
  [["years", "big"], "YRS"],
  [["year", "big"], ""]
]

const getPlaceholder = (type, size) =>
  placeholders.find((p) => {
    const [match] = p
    const [t, s] = match
    return (t === type || t === null) && s === size
  })[1]

const formatSecondaryValue = (secondValue, mainUnits) =>
  mainUnits === "%" || mainUnits === "percent" || mainUnits === "percent-100"
    ? util.formatDollarsNoCents(secondValue)
    : (Math.round(secondValue * 10000) / 100).toString().concat("%")

const formatValue = ({value, secondaryValue, units, valueType}) => {
  if (secondaryValue) {
    const secondVal = formatSecondaryValue(secondaryValue, units)
    if (units === "$") {
      return `${util.formatDollarsNoCents(value)} (${secondVal})`
    } else if (units === "%") {
      return `${Math.round(value * 10000) / 100}% (${secondVal})`
    }
  } else if (units === "$") {
    return util.formatDollarsNoCents(value)
  } else if (units === "%") {
    if (isNaN(value)) {
      return `0${units}`
    } else {
      const places = Math.round(value * 100000) % 2 === 0 ? 2 : 3
      // rounding to account for tiny decimal discrepancies in the math that popup periodically
      return valueType === "percent-no-decimal" ? `${value * 100}${units}` : `${(value * 100).toFixed(places)}${units}`
    }
  } else {
    return `${value} ${units}`
  }
}

const Value = ({value, secondaryValue, units, valueType, color, disabled}) => (
  <span className={styles["value"]} style={{color: `#${disabled ? "dbdee0" : color}`}} readOnly>
    {formatValue({
      value,
      secondaryValue,
      units,
      color,
      disabled,
      valueType
    })}
  </span>
)

const DirectText = ({id, disabled, value, valueType, secondaryValue, onChange}) => {
  const className = secondaryValue && id === "retirement-calculator-retirement-goal" ? cx(styles["text-input"], styles["smaller"]) : styles["text-input"]
  const digitsOnly = ["years-full", "years", "months"]
  return (
    <div className={cx(styles["direct-text"], secondaryValue && styles["has-secondary"])}>
      <MaskedInput
        className={className}
        disabled={disabled}
        type="text"
        value={value}
        mask={valueTypeOptions[valueType].mask}
        onChange={(event) => (digitsOnly.includes(valueType) ? onChange(util.digitsOnly(event.target.value)) : onChange(util.stripChars(event.target.value)) || 0)}
        inputMode="decimal"
      />
      {secondaryValue ? <span className={styles["secondary-value"]}>{`(${formatSecondaryValue(secondaryValue, valueType)})`}</span> : null}
    </div>
  )
}

const Value_ = ({id, onChange, disabled, value, units, valueType, secondaryValue, color, directText}) => {
  return (value || units) && directText ? (
    <DirectText id={id} disabled={disabled} value={value} units={units} valueType={valueType} secondaryValue={secondaryValue} onChange={onChange} />
  ) : value || units ? (
    <Value disabled={disabled} value={value || 0} units={units || "Units"} valueType={valueType} secondaryValue={secondaryValue} color={color} />
  ) : null
}

export const Label = ({id, tooltipText, value, units, secondaryValue, color, className, customClass, disabled, inputId, valueType, directText, onChange, children}) => {
  return (
    <label id={`${id}-label`} className={cx(styles["label"], className && styles[className], customClass, fonts.getFontClass())} htmlFor={inputId}>
      <div>
        {children}
        {tooltipText && <Tooltip>{tooltipText}</Tooltip>}
      </div>
      <Value_
        id={id}
        disabled={disabled}
        value={value}
        units={units}
        valueType={valueType}
        secondaryValue={secondaryValue}
        color={color}
        directText={directText}
        onChange={onChange}
      />
    </label>
  )
}

const Title = ({title, children, className, tooltipText, inputId, showIcon, size}) => {
  const cls = size === "small" ? "calc-input-with-title" : "calc-input"
  return (
    <div className={cx(styles[cls], showIcon && styles["input-icon"], fonts.getFontClass())}>
      <Label inputId={inputId} className={className} tooltipText={tooltipText}>
        {title}
      </Label>
      {children}
    </div>
  )
}

const SmallTitle = (props) => <Title {...assign({}, props, {size: "small"})} />

const Untitle = ({children}) => children

const titles = {
  [null]: Untitle,
  big: Title,
  small: SmallTitle
}

/*
 * Smallest possible atom representing an input
 */
export const Input = ({id, title, value, placeholder, onChange, onKeyPress, tooltipText, inputMode, valueType = "currency", size = "small"}) => {
  const has = hasValue(value)
  const inputId = `${id}-${str.toKebab(title)}`
  return React.createElement(
    titles[title ? size : null],
    {title, tooltipText, inputId},
    <MaskedInput
      className={cx(has && styles["active"], styles[size], styles["input"], fonts.getFontClass())}
      type="text"
      id={id && inputId}
      value={value}
      mask={valueTypeOptions[valueType].mask}
      onKeyPress={onKeyPress}
      onChange={(event) => onChange(util.stripChars(event.target.value))}
      placeholder={placeholder || getPlaceholder(valueType, size)}
      inputMode={inputMode}
      aria-label={title}
    />
  )
}

/* Older input implementations we should slowly start switching to use Input ^^ */

const CalcInput__ = ({
  id,
  value,
  type,
  placeholder,
  onChange,
  onKeyPress,
  size,
  mask,
  integerLimit,
  autoFocus,
  includeIcon,
  allowZero = false,
  className,
  inputMode,
  ariaDescribedBy,
  ...props
}) => {
  const empty = value === null || value === undefined || value === "" || (value === "0" && !allowZero)
  const limit = integerLimit || 9
  return (
    <MaskedInput
      className={cx(includeIcon && styles["input-icon"], !empty && styles["active"], styles[size], styles["input"], className && className, fonts.getFontClass())}
      id={id && id}
      mask={(type === "%" ? maskWithPercent : mask || amtMask)(limit)}
      type="text"
      autoFocus={autoFocus}
      onChange={(event) => onChange(event, util.stripChars)}
      onKeyPress={onKeyPress}
      placeholder={placeholder || "———,———"}
      value={value === "0" && allowZero ? 0 : empty ? "" : util.fromIntNoCents(value)}
      inputMode={inputMode || "decimal"}
      aria-describedby={ariaDescribedBy}
      {...props}
    />
  )
}

const _CalcInput = ({id, className, title, value, onChange, onKeyPress, placeholder, integerLimit, includeIcon, type, inputMode, ariaDescribedBy}) => {
  const inputId = `${id}-${str.toKebab(title)}`
  return (
    <div className={cx(styles["calc-input"], className && className, includeIcon && styles["input-icon"], fonts.getFontClass())}>
      <Label inputId={inputId} htmlFor={inputId}>
        {title}
      </Label>
      <CalcInput__
        id={inputId}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onKeyPress={onKeyPress}
        integerLimit={integerLimit}
        includeIcon={includeIcon}
        inputMode={inputMode}
        ariaDescribedBy={ariaDescribedBy}
      />
    </div>
  )
}

const _SmallCalcInput = (props) => <CalcInput__ className={props.className} size="small" mask={maskWithSymbol} placeholder="$" {...props} />

const _SmallCalcInputWithTitle = (props) => {
  const {title, tooltipText, mask, type, id} = props
  const inputId = `${id}-${str.toKebab(title)}`
  return (
    <div className={styles["calc-input-with-title"]}>
      <Label inputId={inputId} className="inline" tooltipText={tooltipText}>
        {title}
      </Label>
      {React.createElement(
        CalcInput__,
        assign({}, props, {
          id: inputId,
          size: "small",
          mask: mask ? mask : maskWithSymbol,
          placeholder: type ? type : "$"
        })
      )}
    </div>
  )
}

const _LongCalcInput = (props) => <CalcInput__ className={styles["calc-input-long"]} size="long" mask={maskWithSymbol} placeholder="$" {...props} />

const _RangeInput = ({
  disabled,
  title,
  units,
  min,
  max,
  step,
  value,
  onChange,
  secondaryValue,
  directText = true,
  tooltipText,
  valueType = "currency",
  color,
  id,
  customClass,
  inputCls
}) => {
  const _id = `${id}-${str.toKebab(title)}`

  const handleOnChange = (event) => {
    onChange(event ? event : 0)
  }

  return (
    <div className={cx(styles[customClass], styles["calc-input"], disabled && styles["disabled"], directText && styles["with-text-input"], styles["range"])}>
      <Label
        id={_id}
        onChange={disabled ? () => null : handleOnChange}
        tooltipText={tooltipText}
        value={value}
        units={units}
        valueType={valueType}
        secondaryValue={secondaryValue}
        color={color}
        disabled={disabled}
        directText={directText}
        customClass={inputCls}
      >
        {title}
      </Label>
      <input
        className={fonts.getFontClass()}
        id={_id}
        type="range"
        onChange={disabled ? () => null : handleOnChange}
        value={value}
        min={min}
        max={max}
        step={step}
        aria-label={`Range for ${title}`}
      />
    </div>
  )
}

const StandardInput = (Component, defaultValue) => {
  return class extends React.Component {
    constructor(props) {
      super(props)
      this.state = {
        value: props.initialValue || defaultValue
      }
      this.onChange = this.onChange.bind(this)
    }

    componentDidUpdate(prevProps) {
      const {resetKey, initialValue} = this.props
      if (resetKey !== prevProps.resetKey) {
        this.setState({value: initialValue || defaultValue})
      } else if (initialValue !== prevProps.initialValue) {
        this.setState({value: initialValue || defaultValue})
      }
    }

    /*
     * Would have been nice to simply take a value; instead
     * we'll remain backwards compatible.
     */
    onChange(eventOrValue, modifierFn) {
      let value
      if (eventOrValue && typeof eventOrValue === "object") {
        value = (modifierFn || ((v) => v))(eventOrValue.target.value)
      } else {
        value = eventOrValue
      }
      this.setState({value})
      this.props.onChange(value)
    }

    render() {
      return (
        <Component
          {...merge({}, this.props, {
            onChange: this.onChange,
            value: this.state.value
          })}
        />
      )
    }
  }
}

const _Dropdown = ({options = [], color, value, onChange, className, arrowClassName, id, label}) => {
  return (
    <div className={cx(styles["dropdown"], className && className)}>
      {!color || <span className={styles["dot"]} style={{background: color}} />}
      <select className={cx(styles["select"], fonts.getFontClass())} onChange={onChange} value={value} id={id && `${id}-select`} aria-label={label}>
        {options.map((entry, index) => {
          if (Array.isArray(entry)) {
            const [_value, text] = entry
            return (
              <option key={_value} value={_value}>
                {text}
              </option>
            )
          } else {
            return <option key={index}>{entry}</option>
          }
        })}
      </select>
      <i className={cx(arrowClassName, styles["down-arrow-icon"])} />
      <div className={styles["dropdown-background"]}></div>
    </div>
  )
}

export const Dropdown = StandardInput(_Dropdown, "")
export const CalcInput = StandardInput(_CalcInput, "")
export const SmallCalcInput = StandardInput(_SmallCalcInput, "")
export const SmallCalcInputWithTitle = StandardInput(_SmallCalcInputWithTitle, "")
export const LongCalcInput = StandardInput(_LongCalcInput, "")
export const RangeInput = StandardInput(_RangeInput, "")

export class ExpenseFormEntry extends React.Component {
  constructor(props) {
    super(props)
    this.valueChange = this.valueChange.bind(this)
    this.handleOnEnter = this.handleOnEnter.bind(this)
  }

  valueChange(stringValue) {
    const value = parseInt(stringValue, 10) || 0
    this.props.onValueChange(value)
  }

  handleOnEnter(event) {
    const {onPressEnter} = this.props
    if (onPressEnter && event.key === "Enter") {
      onPressEnter()
    }
  }

  render() {
    const {color, initialName, value, options, onNameChange, resetKey, autoFocus, integerLimit, locked, name, className, arrowClassName, containerClassName} = this.props
    const id = `${str.toKebab(name)}`
    return (
      <div className={cx(styles["entry"], containerClassName)}>
        <label>
          <div className="sr-only">{`Budget Category: ${name}`}</div>
          <Dropdown
            id={id}
            name={name}
            className={className}
            arrowClassName={arrowClassName}
            options={locked ? [name] : options}
            color={color}
            initialValue={initialName}
            onChange={onNameChange}
            resetKey={resetKey}
          />
        </label>
        <label className="sr-only" htmlFor={`${id}-small-calc-input`}>
          {`Budget Amount for ${name}`}
        </label>
        <SmallCalcInput
          id={`${id}-small-calc-input`}
          className={styles["calc-input-small"]}
          initialValue={value}
          onChange={this.valueChange}
          onKeyPress={this.handleOnEnter}
          autoFocus={autoFocus}
          resetKey={resetKey}
          integerLimit={integerLimit}
        />
      </div>
    )
  }
}

export const newStickyIndex = () => []

const expenseFormInitialState = (initialData, component) => {
  let stickyIndex = newStickyIndex()
  const state = {
    data: initialData.map((e, i) => {
      return ((c) => {
        const {id, indexState} = util.claim(stickyIndex)
        if (c.props.onRowChange) {
          c.props.onRowChange({index: i, id})
        }
        stickyIndex = indexState
        return {id, takenOption: e.name}
      })(component)
    }),
    setFocusIndex: -1
  }
  state.stickyIndex = stickyIndex

  return state
}

const formDefaults = {
  options: ["Option"],
  min: 4,
  max: 16
}

const isOptionLocked = (lockedOptions, option) => lockedOptions.some((o) => o === option)

export class ExpenseForm extends React.Component {
  constructor(props) {
    super(props)
    this.initialData = this.props.initialValue || []
    this.state = expenseFormInitialState(this.initialData, this)
    this.onChange = this.onChange.bind(this)
    this.addEntry = this.addEntry.bind(this)
  }

  componentDidUpdate(prevProps) {
    const {resetKey} = this.props
    if (resetKey !== prevProps.resetKey) {
      this.setState(expenseFormInitialState(this.initialData, this))
    }
  }

  onChange(change) {
    const {name, cost, index} = change

    this.setState((prevState) => {
      const newData = prevState.data.slice()
      let indexState = prevState.stickyIndex
      if (name === "[X] Delete") {
        indexState = util.free(indexState, newData[index].id)
        if (this.props.onRowRemove) {
          this.props.onRowRemove(index)
        }
        newData.splice(index, 1)
      } else {
        if (name !== undefined) {
          newData[index].takenOption = name
        }
        if (this.props.onRowChange) {
          this.props.onRowChange({name, cost, index})
        }
      }

      return {data: newData, stickyIndex: indexState}
    })
  }

  getOptions() {
    return this.props.options || formDefaults.options
  }

  addEntry() {
    this.setState((prevState) => {
      const {max = Math.max(this.initialData.length, formDefaults.max)} = this.props
      if (prevState.data.length < max) {
        const {id, indexState} = util.claim(prevState.stickyIndex)
        if (this.props.onRowAdd) {
          this.props.onRowAdd({index: prevState.data.length, id})
        }
        return {data: prevState.data.concat([{id}]), stickyIndex: indexState, setFocusIndex: prevState.data.length}
      }
    })
  }

  render() {
    const {data, setFocusIndex} = this.state
    const {min = Math.min(this.initialData.length, formDefaults.min), max = Math.max(this.initialData.length, formDefaults.max), resetKey, integerLimit, dataTestId} = this.props
    const {title, colors, className, entryClassName, arrowClassName, containerClassName} = this.props
    const options = this.getOptions()
    const availableOptions = options.slice().filter((o) => o === "Other" || data.findIndex((d) => d.takenOption === o) === -1)
    const locked = (option) => (this.props.lockedOptions ? isOptionLocked(this.props.lockedOptions, option) : false)
    const deleteOption = data.length > min ? ["[X] Delete"] : []
    const atMaximum = data.length >= max
    const labelId = str.toKebab(title)
    return (
      <div className={cx(styles["expense-form"], className && className)} data-testid={dataTestId || labelId}>
        <Label id={labelId}>{title}</Label>
        {data.map((e, index) => {
          const {cost} = this.initialData[index] || {}
          const currentName = e.takenOption || "Select a Category"
          return (
            <ExpenseFormEntry
              labelId={labelId}
              className={entryClassName}
              containerClassName={containerClassName}
              arrowClassName={arrowClassName}
              color={colors ? colors[e.id] : null}
              key={e.id}
              name={currentName}
              value={cost}
              integerLimit={integerLimit}
              options={[currentName].concat(deleteOption, availableOptions)}
              locked={locked(e.takenOption)}
              onNameChange={(newName) => this.onChange({index, name: newName})}
              onValueChange={(newValue) => this.onChange({index, cost: newValue})}
              onPressEnter={this.addEntry}
              resetKey={resetKey}
              autoFocus={index === setFocusIndex}
            />
          )
        })}
        {!atMaximum && (
          <button className={cx(styles["add-entry"], fonts.getFontClass())} onClick={this.addEntry} data-testid="add-another">
            <i />
            Add another...
          </button>
        )}
      </div>
    )
  }
}
